/** Componente ListaForumComentarios responsável por exibir a lista de comentários de um fórum específico. Ele utiliza o React Router para navegar entre as páginas e exibir os comentários relacionados a um determinado tópico do fórum. 
 * Recebe os dados do tópico do fórum via state e exibe o assunto, comentário, nome do usuário e a foto do usuário que fez o comentário, trazendo abaixo a lista de comentários relacionados ao tópico selecionado.
 * 
 * 
 */
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import fotoIndisponivel from "../../assets/imagens/indisponivel.png";
import { useAuth } from "../../contextos/AuthContext"; // Importa o hook useAuth do contexto de autenticação para verificar se o usuário está logado e obter informações do usuário, como o token de autenticação.  
import Botao from "../Botao"; // Importa o componente Botao para criar botões estilizados na interface do usuário.
import CadastroForum from "../CadastroForum"; // Importa o componente CadastroForum para permitir que os usuários criem novos tópicos ou comentários no fórum.
const ListaForumComentarios = () => {
    // Recupera o token do LocalStorage
    const {token} = useAuth();
    const location = useLocation(); // Hook para acessar o estado passado pela rota, que pode conter os dados do usuário logado ou outros dados relevantes para a exibição do fórum
    const navigate = useNavigate(); // Hook para redirecionar o usuário para outras páginas, como a página de resultado em caso de erro
    const mensagem = useLocation().state; // Recupera os dados do tópico do fórum passados via state
    // Extrai o nome antes do @ do email
    //Use o operador de coalescência nula (??). Ele só assume o valor padrão se o dado for estritamente null ou undefined, permitindo que a string vazia passe.
    const statusFiltro = location.state?.status ?? 'publicado'; // Obtém o status de filtro da localização, ou define como 'publicado' se não estiver presente
    const [mensagens, setMensagens] = useState([]); // Estado para armazenar a lista de mensagens, inicialmente vazio
    const [carregando, setCarregando] = useState(true); // Estado para controlar o carregamento dos dados, inicialmente verdadeiro
    // O estado do Modal vive aqui no "pai" de todos. Controla se o modal de cadastro do fórum está aberto ou fechado
    const [modalCadastroForumAberto, setModalCadastroForumAberto] = useState(false);


/* Esta função roda uma vez quando o componente é montado
/* Carrega a lista de mensagens relacionadas ao tópico do backend assim que o componente for exibido, garantindo que tenhamos os dados para mostrar na tela */    
useEffect(() => {

    // Criamos um controlador de abort para permitir o cancelamento da requisição caso o componente seja desmontado antes da conclusão da requisição, evitando erros de memória e chamadas desnecessárias ao backend
    const controller = new AbortController();
    //Criamos uma função assíncrona para buscar as mensagens do backend, incluindo o token de autenticação no cabeçalho da requisição. Tem que ser criada dentro do useEffect porque o useEffect não pode ser assíncrono diretamente, mas pode chamar funções assíncronas. 
    const buscarMensagens = async () => {
        
        try{
            // Faz a requisição para o backend para buscar as mensagens, incluindo o token no cabeçalho de autorização
            //Usa a URL da sua variável de ambiente e completa a URL da requisição para buscar as mensagens, incluindo o status de filtro como parâmetro de consulta e o idResposta. O backend pode então usar esse parâmetro para filtrar as mensagens de acordo com o status desejado (enviado, liberado, bloqueado, etc.) trazendo as mensagens correspondentes.

            // Proteção contra acesso direto pela URL
            if (!mensagem) {
                navigate('/resultado', { 
                    state: { 
                    status: 'aviso',
                    mensagem: 'Para acesso aos comentários precisa acessar através do Fórum.' 
                          }
                    }); 
                return null;
            }

            const endpoint = `/forum-list?status=${statusFiltro}&idResposta=${mensagem.id}`  // Define o endpoint 

            const url = `${process.env.REACT_APP_API_URL}${endpoint}`;
            
            const resposta = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    // 3. Envia o token via Header Authorization
                    'Authorization': `Bearer ${token}`
                },
                signal: controller.signal // ✅ Vincula o abort ao fetch
            });


            if (!resposta.ok) {
                    // Se o backend retornar 401 ou 403, algo está errado com a permissão
                    throw new Error(`Erro na requisição: ${resposta.status}`);
                }
            
            const dadosRetorno = await resposta.json();
        
            if (dadosRetorno.status === 'sucesso') {
                setMensagens(dadosRetorno.dados); // Salva as mensagens no estado para que os dados originalmente recebidos do backend estejam disponíveis para renderização e outras operações
                
                console.log("Mensagens recebidas do backend:", dadosRetorno.dados); // Log para verificar os dados recebidos
                setCarregando(false); // Define o estado de carregamento como falso após receber os dados
            } else {
                navigate('/resultado', { 
                    state: { 
                    status: dadosRetorno.status,
                    mensagem: dadosRetorno.mensagem
                          }
                    });   
            }
        }catch(error){

            if (error.name === 'AbortError') return; // ✅ 1ª execução cancelada silenciosamente
            navigate('/resultado', { 
                    state: { 
                    status: "erro",
                    mensagem: "Ocorreu um erro ao buscar as mensagens: " + error.message
                          }
                    });
        }
    }

    buscarMensagens(); // Chama a função para buscar as mensagens quando o componente for montado

    return () => controller.abort(); // ✅ Cancela quando desmonta (1ª execução do StrictMode)

}, []);

// caso os dados do backend ainda estejam sendo carregados, exibe um spinner de carregamento para informar ao usuário que os dados estão sendo processados. Isso melhora a experiência do usuário, evitando que ele veja uma tela em branco ou incompleta enquanto os dados são carregados.
if (carregando) {
    return (
        <div className="container-fluid text-center mt-5">
            <div className="spinner-border text-success" role="status">
                <span className="visually-hidden">Carregando...</span>
            </div>
        </div>
    );
}

    // Separa a mensagem principal das respostas
    const mensagemPrincipal = mensagens.find(c => c.idResposta === null);
    
    const nomeUsuarioPrincipal = (mensagemPrincipal.usuario || '').split('@')[0]; // Extrai o nome antes do @ do email
                    // Define a foto, usando a imagem padrão se não houver foto cadastrada
    const urlFotoPrincipal = mensagemPrincipal.foto ? `${process.env.REACT_APP_API_URL}/public/assets/imagens/usuarios/${mensagemPrincipal.foto}`: fotoIndisponivel; // Usa a imagem padrão se não houver foto cadastrada }

    const respostas = mensagens.filter(c => c.idResposta !== null);

    return (
        <div className="container-fluid justify-content-center">
            <h1 className="h1 text-center mb-5">Fórum de Estudos - Comentários</h1>
            <div className="container-fluid mb-3 bg-dark-subtle p-3 rounded">
                {/* Renderiza os dados da mensagem principal - Mensagem principal — idResposta null */}
                {mensagemPrincipal && (
                        <div  className="container-fluid d-flex mb-3 bg-dark-subtle p-3 rounded ">   
                            <div className="d-flex flex-column align-items-center justify-content-center mb-2 gap-2">
                                    <img 
                                    src={urlFotoPrincipal} 
                                    alt={`Foto de ${nomeUsuarioPrincipal}`}
                                    style={{ width: '45px', height: '45px', objectFit: 'cover' }}
                                    className="rounded-circle border"
                                    />
                                    <span className="fw-bold fs-6">@{nomeUsuarioPrincipal}</span>
                            </div>

                            <div className="d-flex flex-column ms-3">
                                <h2 className="h2">{mensagemPrincipal.assunto}</h2>
                                <p className="text-start fs-5" style={{ whiteSpace: 'pre-line' }}>{mensagemPrincipal.comentario}</p>
                                <small className="text-muted">{mensagemPrincipal.data}</small>
                            </div>
                        </div>
                        )}
                

                {/* Renderiza os dados das mensagens relacionadas ao tópico selecionado */}

                {respostas.length === 0 ? (
                    <div className="container-fluid d-flex mb-3 bg-light border border-success p-3 rounded">
                        <p className="text-muted fs-3">Nenhum comentário encontrado. Seja o primeiro a comentar!</p>
                    </div>

                ) : (

                    /* Neste ponto vai renderizar cada resposta encontrada no backend, exibindo a foto do usuário, o nome do usuário (extraído do email), o comentário e a data da resposta. Cada resposta é renderizada dentro de um container estilizado para manter a consistência visual com a mensagem principal. */
                    
                    respostas.map((resposta) => {
                                const nomeUsuario = (resposta.usuario || '').split('@')[0];
                                const urlFoto = resposta.foto
                                    ? `${process.env.REACT_APP_API_URL}/public/assets/imagens/usuarios/${resposta.foto}`
                                    : fotoIndisponivel;

                                return (
                                    <div key={resposta.uuid} className="container-fluid d-flex mb-3 bg-light border border-success p-3 rounded border border-white border-1">
                                        <div className="d-flex flex-column align-items-center justify-content-center mb-2 gap-2">
                                            <img
                                                src={urlFoto}
                                                alt={`Foto de ${nomeUsuario}`}
                                                style={{ width: '45px', height: '45px', objectFit: 'cover' }}
                                                className="rounded-circle border"
                                            />
                                            <span className="fw-bold fs-6">@{nomeUsuario}</span>
                                        </div>
                                        <div className="d-flex flex-column ms-3">
                                            <span>
                                                {/* Seta indicando resposta */}
                                                <div style={{
                                                    borderBottom: '2px solid #198754',
                                                    borderLeft: '2px solid #198754',
                                                    width: '20px',
                                                    height: '20px',
                                                    marginTop: '8px',
                                                    marginRight: '8px',
                                                    flexShrink: 0
                                                }}/>
                                            </span>
                                        </div>
                                        <div className="d-flex flex-column ms-3">
                                            <p className="text-start fs-5" style={{ whiteSpace: 'pre-line' }}>{resposta.comentario}</p>
                                            <small className="text-muted">{resposta.data}</small>
                                        </div>
                                    </div>
                                );
                            })
                        )}

                        {token ? (
                            <>
                           
                            <Botao
                                //Ao clicar no botão, abre o modal de cadastro de novo tópico, setando o estado para true
                                aoClicar={() => 
                                    setModalCadastroForumAberto(true)
                                } // Redireciona para a página de cadastro de novo tópico
                                className="btn btn-success btn-lg mb-3"
                            >
                                Comentar  
                            </Botao>
                            </>   

                        ) : (
                                        
                        null)}
            </div>


            <CadastroForum
                /*Importante - ao passar estes parâmetros para o componente CadastroForum estamos elevando o estado destas variáveis para este componente pai ListaForum. Isto permite que o ListaForum controle a exibição do modal e reaja ao sucesso do Cadastro */
                // Controla a exibição do modal conforme o estado
                exibir={modalCadastroForumAberto} 
                aoFechar={() => setModalCadastroForumAberto(false)} 
                topico={mensagemPrincipal?.assunto}
                idResposta={mensagemPrincipal?.id}
            />
        </div>    
    )}

export default ListaForumComentarios;